import { supabase } from '@/core/supabase/client';

/**
 * Migración del histórico.
 *
 * Todo lo que decide se decide en la base: acá sólo se lee el estado del lote y
 * se disparan las acciones que ya validan permisos y dejan auditoría. Nada de
 * esta capa infiere ni fusiona nada.
 */

export interface ImportBatch {
  id: string;
  source: string;
  cutoff_date: string;
  extracted_at: string;
  notes: string | null;
  created_at: string;
}

export interface ClientMapping {
  id: string;
  legacy_text: string;
  occurrences: number;
  client_id: string | null;
  establishment_id: string | null;
  confidence: 'exact' | 'probable' | 'ambiguous';
  trailing_number: number | null;
  proposed_km: number | null;
  km_evidence: unknown;
  resolved_at: string | null;
  notes: string | null;
}

export type ClasificacionDiferencia =
  | 'exacta'
  /** Defecto conocido del sistema anterior. */
  | 'bug_legacy'
  /** Redondeo o normalizacion, explicado y acotado. */
  | 'normalizacion'
  /** Dato ambiguo todavia sin resolver. */
  | 'pendiente'
  /** Cualquier otra cosa. BLOQUEA la migracion. */
  | 'error_migracion';

export interface ReconciliationRow {
  id: string;
  scope: 'periodo' | 'trabajo' | 'participante' | 'facturador';
  metric: string | null;
  source_row_id: string;
  billing_year: number;
  billing_month: number;
  participant_id: string | null;
  legacy_cents: number | null;
  computed_cents: number | null;
  diff_cents: number | null;
  kind: ClasificacionDiferencia;
  reason: string | null;
}

export async function listBatches(organizationId: string): Promise<ImportBatch[]> {
  const { data, error } = await supabase
    .from('legacy_import_batches')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as ImportBatch[];
}

/** Sin resolver primero: son las que esperan una decisión humana. */
export async function listClientMappings(batchId: string): Promise<ClientMapping[]> {
  const { data, error } = await supabase
    .from('legacy_client_map')
    .select('*')
    .eq('batch_id', batchId)
    .order('resolved_at', { ascending: true, nullsFirst: true })
    .order('occurrences', { ascending: false });
  if (error) throw error;
  return (data ?? []) as ClientMapping[];
}

export async function listReconciliation(batchId: string): Promise<ReconciliationRow[]> {
  const { data, error } = await supabase
    .from('legacy_reconciliation')
    .select('*')
    .eq('batch_id', batchId)
    .order('scope')
    .order('billing_year')
    .order('billing_month');
  if (error) throw error;
  return (data ?? []) as ReconciliationRow[];
}

export async function reconcileBatch(batchId: string): Promise<unknown> {
  const { data, error } = await supabase.rpc('reconcile_legacy_batch', { p_batch: batchId });
  if (error) throw error;
  return data;
}

/**
 * Resuelve a qué cliente corresponde un texto legacy. Exige explicar en qué se
 * basa la decisión: queda en la bitácora junto al texto original.
 */
export async function resolveClient(input: {
  mappingId: string;
  clientId: string;
  establishmentId?: string | null;
  reason: string;
}): Promise<{ trabajos_movidos: number; trabajos_bloqueados: number }> {
  const { data, error } = await supabase.rpc('resolve_legacy_client', {
    p_map: input.mappingId,
    p_client: input.clientId,
    p_establishment: input.establishmentId ?? null,
    p_reason: input.reason,
  });
  if (error) throw error;
  return data as { trabajos_movidos: number; trabajos_bloqueados: number };
}

/**
 * Sólo se puede aceptar con evidencia EXTERNA registrada. El número del nombre
 * no alcanza, y coincidir con los km del propio legacy tampoco: sería el mismo
 * dato mirándose al espejo.
 */
export async function acceptProposedKm(mappingId: string): Promise<unknown> {
  const { data, error } = await supabase.rpc('accept_proposed_km', { p_map: mappingId });
  if (error) throw error;
  return data;
}

/** Registra evidencia EXTERNA de kilometraje. El legacy no vale como fuente. */
export async function recordKmEvidence(input: {
  mappingId: string;
  km: number;
  source: string;
  detail?: unknown;
}): Promise<unknown> {
  const { data, error } = await supabase.rpc('record_km_evidence', {
    p_map: input.mappingId,
    p_km: input.km,
    p_source: input.source,
    p_detail: input.detail ?? null,
  });
  if (error) throw error;
  return data;
}

/** Todo lo que la migracion NO resolvio sola, junto y a la vista. */
export async function fetchReview(batchId: string): Promise<Record<string, unknown>> {
  const { data, error } = await supabase.rpc('legacy_review', { p_batch: batchId });
  if (error) throw error;
  return data as Record<string, unknown>;
}

export async function reclassifyEstablishment(
  workId: string,
  establishmentId: string,
  reason: string,
): Promise<void> {
  const { error } = await supabase.rpc('reclassify_establishment', {
    p_work: workId,
    p_establishment: establishmentId,
    p_reason: reason,
  });
  if (error) throw error;
}
